import React, { useEffect, useRef, useState } from 'react'
import {
  CheckoutBelowButtonText,
  CheckoutBelowButtonWrapper,
  CheckoutCreditCardDetailsDesc,
  CheckoutCreditCardDetailsTitle,
  CheckoutCreditCardDetailsWrapper,
  StripeFormCountryAndState,
  StripeFormAddressSection,
  ToggleLabel,
  ToggleWrapper,
  InputStyles,
  CityAndZipCodeRowContainer,
  AccountTypeSection,
  businessTooltipStyles,
  vatTooltipStyles,
  tooltipTriggerStyles,
} from 'Features/checkout/views/Checkout/Checkout.styled'
import { isMobile } from 'react-device-detect'
import { getAmountWithCurrency } from 'Utils/currencyHelper'
import { StripeWrapper } from 'Features/checkout/components/StripeForm/StripeForm'
import {
  AsyncSelectCq,
  Button,
  GeneralSelect,
  Input,
  Toggle,
  Tooltip,
} from '@clickcease/cc-client-components-lib'
import { Countries } from 'Assets/lists/countries'
import { SelectOption } from 'Types/SelectOption'
import States from 'Assets/lists/usStates'
import {
  CheckoutCreditCardCancelAnytime,
  StripeFormError,
} from 'Features/checkout/components/StripeForm/Stripe.styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLock } from '@fortawesome/pro-light-svg-icons'
import { useAddSubscription } from 'Features/checkout/api/addSubscription'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Coupon, CouponType } from 'Features/checkout/components/Coupon/Coupon'
import { useVerifyUrlCodeFromParams } from 'Features/auth/hooks/useVerifyUrlCodeFromParams'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useGetPlans } from 'Features/pricing/api/getPlans'
import { PlanDetails } from 'Features/pricing/types/PlanDetails'
import { usePaymentsFlowSplit } from 'Hooks/split/usePaymentsFlow'
import { usePricingEndOfTrial } from 'Hooks/split/usePricingEndOfTrial'
import { useUpdatePlan } from 'Features/CrossSell/CrosSellPricing/api/useUpdatePlan'
import { GLOBAL_MODALS } from 'Types/GlobalModal'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { PARAMS_COOKIE_KEYS } from 'Consts/cookies.enum'
import { CookieHelper } from 'Utils/cookieHelper'
import { useUserStore } from 'Stores/userStoreModuleFed'
import {
  UpdateBillingDetailsApiResponse,
  useUpdateAccountSettings,
} from 'Features/accountSettings/api/updateAccountSettings'
import { useSplitWithConfig } from 'Hooks/useSplitWithConfig'
import {
  CheckoutCardDetailsWrapperProps,
  CheckoutFormState,
} from './CheckoutCardDetailsWrapper.models'
import axios from 'axios'
import { ServerError } from 'Lib/axios'
import { useGetUserAccounts } from 'Features/AccountSelector/api/getUserAccounts'
import { useGoogleAddressSearch } from 'Hooks/useGoogleAddressSearch'

const subscriptionAlreadyExists = 'ACCOUNT_HAVE_ACTIVE_SUBSCRIPTION'
const couponsNotAllowed = 'COUPONS_NOT_ALLOWED'

export const CheckoutCardDetailsWrapper = ({
  planIds,
  currency,
  productsSelected,
  setFinishedCheckout,
  onValidateCoupon,
  onToggleMonthly,
  partnerTrialDays,
  isFetching,
  reactivation,
  reactivationSameProduct,
  billingCycleMonths,
  partner,
}: CheckoutCardDetailsWrapperProps) => {
  const { t } = useTranslation('checkout')
  const addSubscription = useAddSubscription()
  const updatePlan = useUpdatePlan()
  const navigate = useNavigate()
  const submitRef = useRef<HTMLButtonElement>()

  const accountTypes = [
    { label: t('business_entity'), value: t('business_entity') },
    { label: t('personal_account'), value: t('personal_account') },
  ]

  const [formState, setFormState] = useState<CheckoutFormState>({
    country: '',
    city: '',
    zipCode: '',
    address: '',
    state: '',
    accountType: '',
    companyVatId: '',
    addressSearchValue: null,
  })

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isMonthlyBilling, setIsMonthlyBilling] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const { getSessionStorageItem } = useVerifyUrlCodeFromParams()
  const [coupon, setCoupon] = useState<CouponType>()
  const isFeatureEnabled = usePaymentsFlowSplit()
  const plans = useGetPlans()
  const renderCoupon = getSessionStorageItem('cpncd')
  const pricingEndOfTrial = usePricingEndOfTrial()
  const paymentFlow = usePaymentsFlowSplit()
  const { updateLogicCookieData } = CookieHelper()
  const { setModalToShow, setDisablePlatformConnectionModal } = useAppStore()
  const { setIsReactivation } = useUserStore()
  const { mutateAsync: setUserBillingDetails, isLoading: isLoadingUpdateBillingDetails } =
    useUpdateAccountSettings()
  const { data: userAccountData } = useGetUserAccounts()
  const billingDetailsCollectionSplit = useSplitWithConfig(
    'CE_REG_BILLING_DETAILS_COLLECTION',
    userAccountData?.accounts[0]?.created ?? 0
  )
  const hasActiveSubscription = userAccountData.accounts[0]?.activeSubscription
  const {
    loadOptions,
    getPlaceDetails,
    isLoadingSearch,
    setCountry: setGoogleCountry,
    placeDetails,
  } = useGoogleAddressSearch({
    onAddressSelect: (address) => {
      const matchingState = States.find((stateOption) => stateOption.value === address.state)

      setFormState((prev) => ({
        ...prev,
        address: address.street,
        zipCode: address.postalCode,
        city: address.city,
        state: matchingState?.value || '',
        addressSearchValue: address.street
          ? {
              label: address.street,
              value: address.street,
              addressComponents: [],
            }
          : null,
      }))
    },
  })

  const setCountry = (value: string) => {
    setFormState((prev) => ({
      ...prev,
      country: value,
      state: '',
      companyVatId: '',
    }))
    setGoogleCountry(value)
  }

  const setState = (value: string) => {
    setFormState((prev) => ({ ...prev, state: value }))
  }

  const handleInputChange =
    (field: keyof CheckoutFormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormState((prev) => ({
        ...prev,
        [field]: event.target.value,
      }))
    }

  useEffect(() => {
    setIsMonthlyBilling(billingCycleMonths === 1 || false)
  }, [billingCycleMonths])

  const handleMonthlyToggleChange = (currentToggleState: boolean) => {
    setIsMonthlyBilling(currentToggleState)
    onToggleMonthly(currentToggleState)
  }

  const handleBillingDetailsSubmission = async () => {
    try {
      const result = await setUserBillingDetails({
        country: formState.country,
        companyVatId: formState.companyVatId,
        street: placeDetails?.street ?? formState.address,
        zipCode: formState.zipCode,
        city: formState.city,
        ...(formState.country === 'US' && { state: formState.state }),
      })

      if ((result as UpdateBillingDetailsApiResponse).httpStatusCode === 200) {
        setFinishedCheckout(formState.country, formState.state)
        return true
      } else {
        const vatErrorMessage = (result as ServerError).message
        setError(t(vatErrorMessage))
        setIsLoading(false)
        return false
      }
    } catch (error) {
      setError(t('invalid_submitted_billing_details'))
      setIsLoading(false)
      return false
    }
  }

  const onSuccess = async () => {
    setError('')
    if (billingDetailsCollectionSplit) {
      const success = await handleBillingDetailsSubmission()
      if (!success) return
    } else {
      setFinishedCheckout(formState.country, formState.state)
    }
    if (reactivation) {
      navigate('/')
      setDisablePlatformConnectionModal(true)
      updateLogicCookieData(PARAMS_COOKIE_KEYS.IS_REACTIVATION, 'true')
      setIsReactivation(true)
      setModalToShow(GLOBAL_MODALS.DOMAIN_REACTIVATION)
    } else {
      setTimeout(() => {
        navigate('/onboarding')
      }, 2000)
    }
  }

  const onError = async (error) => {
    setIsLoading(false)
    setError(t('check_your_card_details'))

    if (axios.isAxiosError(error) && error.response?.data) {
      const errorData = error.response.data as ServerError
      const message = errorData.message

      if (message === couponsNotAllowed) {
        setError(t(couponsNotAllowed))
        return
      }

      if (message === subscriptionAlreadyExists) {
        setError('')

        if (billingDetailsCollectionSplit) {
          try {
            const success = await handleBillingDetailsSubmission()

            if (success) {
              setFinishedCheckout(formState.country, formState.state)

              setTimeout(() => {
                navigate('/onboarding')
              }, 2000)
            }
          } catch (submissionError) {
            console.error('Billing details submission failed', submissionError)
            setError(t('billing_submission_error'))
          }
        }
      }
    }
  }

  const installmentsPlans: number[] =
    plans.data && isMonthlyBilling && productsSelected.length > 0
      ? productsSelected.reduce((acc: number[], selected: PlanDetails) => {
          const planId = plans.data?.find(
            (plan) =>
              plan.visitsAllowed === selected.allowedVisits &&
              plan.planType === selected.planType &&
              plan.productType === selected.productType &&
              plan.billingCycleMonths === 1 &&
              plan.currency === selected.currency
          )?.id

          if (planId) {
            acc.push(planId)
          }

          return acc
        }, [])
      : []

  const addSubscriptionFunc = (token: string) => {
    addSubscription.mutate(
      {
        token,
        packageIds: isMonthlyBilling ? installmentsPlans : planIds,
        country: formState.country,
        state: formState.state,
        coupon,
        reactivation,
        reactivationSameProduct,
      },
      {
        onSuccess,
        onError,
      }
    )
  }

  const updatedSubscriptionFunc = (token: string) => {
    updatePlan.mutate({ products: planIds, hideErrorToaster: false, token }, { onSuccess, onError })
  }

  const stripeSuccess = async (token: string) => {
    const { country, state, address, city, zipCode, accountType, companyVatId } = formState

    if (country.length === 0) {
      setError(t('country_is_mandatory'))
      setIsLoading(false)
      return
    } else if (country === 'US' && state.length === 0) {
      setError(t('state_is_mandatory'))
      setIsLoading(false)
      return
    } else {
      setError('')
    }

    if (billingDetailsCollectionSplit) {
      if (address.trim().length === 0) {
        setError(t('address_is_mandatory'))
        setIsLoading(false)
        return
      }

      if (city.trim().length === 0) {
        setError(t('city_is_mandatory'))
        setIsLoading(false)
        return
      }

      if (zipCode.trim().length === 0) {
        setError(t('zip_code_is_mandatory'))
        setIsLoading(false)
        return
      }

      if (country !== 'US' && accountType.trim().length === 0) {
        setError(t('account_type_is_mandatory'))
        setIsLoading(false)
        return
      }

      if (accountType === t('business_entity') && companyVatId.trim().length === 0) {
        country === 'IL'
          ? setError(t('company_id_is_mandatory'))
          : setError(t('vat_id_is_mandatory'))
        setIsLoading(false)
        return
      }
    }
    if (hasActiveSubscription) {
      if (billingDetailsCollectionSplit) {
        const success = await handleBillingDetailsSubmission()
        if (!success) return
        else {
          setFinishedCheckout(formState.country, formState.state)
          setTimeout(() => {
            navigate('/onboarding')
          }, 2000)
        }
      }
      return
    }

    if (pricingEndOfTrial && paymentFlow === 'NO_CC' && !reactivation) {
      updatedSubscriptionFunc(token)
    } else {
      addSubscriptionFunc(token)
    }
  }

  const stripeError = (error: string) => {
    setError(error)
    setIsLoading(false)
  }

  const getCountryCss = (country) => {
    if (country === 'US') {
      return isMobile ? '94%' : '217px'
    } else {
      return '100%'
    }
  }

  const handleValidateCoupon = (couponObject: CouponType) => {
    onValidateCoupon(couponObject)
    setCoupon(couponObject)
  }

  const buttonText = () => {
    if (reactivation) {
      return t('reactivate_account')
    } else if (pricingEndOfTrial && paymentFlow === 'NO_CC') {
      return t('stay_protected')
    } else {
      return t('start_your_x_day_free_trial', { trialDays: partnerTrialDays || 7 })
    }
  }

  const getVatPlaceholder = (country: string) => {
    const countryVat = t(`vatCountriesNames.${country}`)
    return countryVat === `vatCountriesNames.${country}` ? t('VatID') : countryVat
  }

  return (
    <CheckoutCreditCardDetailsWrapper>
      <CheckoutCreditCardDetailsTitle reactivation={reactivation}>
        {t('credit_card_details')}
      </CheckoutCreditCardDetailsTitle>
      {!isMobile && !reactivation && (
        <CheckoutCreditCardDetailsDesc>
          {t('you_pay_zero', {
            currency: getAmountWithCurrency(currency || '', undefined),
          })}
        </CheckoutCreditCardDetailsDesc>
      )}
      <StripeWrapper
        onSuccess={stripeSuccess}
        onError={stripeError}
        submitRef={submitRef}
        setIsLoading={(isLoading) => setIsLoading(isLoading)}
        hasActiveSubscription={hasActiveSubscription}
        reactivation={reactivation}
        isLoadingUpdateBillingDetails={isLoadingUpdateBillingDetails}
      />
      <StripeFormCountryAndState>
        <GeneralSelect
          value={Countries.find((item) => item.value === formState.country)}
          options={Countries}
          wrapperCSS={{
            gap: 'unset',
            marginBottom: '16px',
            width: '100%',
            '> div:nth-child(2)': { width: '100%' },
          }}
          selectCss={{
            width: getCountryCss(formState.country),
            height: '38px',
          }}
          onChange={(item: SelectOption) => {
            setCountry(item?.value || '')
          }}
        />
        {formState.country == 'US' && (
          <GeneralSelect
            key={`state-select-${formState.state || 'empty'}`}
            value={States.find((item) => item.value === formState.state)}
            options={States}
            wrapperCSS={{
              gap: 'unset',
              marginBottom: '16px',
              '> div:nth-child(2)': {
                width: '95%',
                display: 'flex',
                justifyContent: 'flex-end',
              },
            }}
            selectCss={{
              width: getCountryCss(formState.country),
              height: '38px',
            }}
            onChange={(item: SelectOption) => {
              setState(item?.value || '')
            }}
          />
        )}
      </StripeFormCountryAndState>
      {formState.country && billingDetailsCollectionSplit && (
        <StripeFormAddressSection>
          <AsyncSelectCq
            wrapperCSS={{
              width: formState.country === 'US' ? '454px' : '100%',
              position: 'relative',
              height: '40px',
              marginBottom: '16px',
            }}
            defaultValue={formState.addressSearchValue}
            onChange={getPlaceDetails}
            loadOptions={loadOptions}
            placeholder={'Search for address'}
            isLoading={isLoadingSearch}
            css={{ height: '38px' }}
          />
          <CityAndZipCodeRowContainer>
            <Input
              name="city"
              css={InputStyles}
              value={formState.city}
              onChange={handleInputChange('city')}
              placeholder={t('city')}
            />
            <Input
              name="zipCode"
              css={InputStyles}
              value={formState.zipCode}
              onChange={handleInputChange('zipCode')}
              placeholder={t('zip_code')}
            />
          </CityAndZipCodeRowContainer>
          <AccountTypeSection>
            {formState.country !== 'US' && (
              <>
                <GeneralSelect
                  value={accountTypes.find((item) => item.value === formState.accountType)}
                  options={accountTypes}
                  placeholder={t('account_type_toggle')}
                  wrapperCSS={{
                    gap: 'unset',
                    '> div:nth-child(2)': {
                      width: '95%',
                      display: 'flex',
                      justifyContent: 'flex-end',
                    },
                  }}
                  selectCss={{
                    width: getCountryCss(formState.country),
                    height: '38px',
                  }}
                  onChange={(item: SelectOption) => {
                    setFormState((prev) => ({
                      ...prev,
                      accountType: item?.value || '',
                      companyVatId: '',
                    }))
                  }}
                />
                <Tooltip
                  tooltiptContent={t('business_account_explainer')}
                  constentStyle={businessTooltipStyles}
                  side={'right'}
                  trigerStyle={{ style: tooltipTriggerStyles(reactivation as boolean) }}
                />
              </>
            )}
            {formState.country !== 'US' && formState.accountType === t('business_entity') && (
              <>
                <Input
                  name="companyVatId"
                  css={{
                    width: '100%',
                    height: '38px',
                  }}
                  value={formState.companyVatId}
                  onChange={handleInputChange('companyVatId')}
                  placeholder={getVatPlaceholder(formState.country)}
                />
                <Tooltip
                  tooltiptContent={t('vat_explainer')}
                  constentStyle={vatTooltipStyles}
                  side={'right'}
                  trigerStyle={{ style: tooltipTriggerStyles(reactivation as boolean) }}
                />
              </>
            )}
          </AccountTypeSection>
        </StripeFormAddressSection>
      )}
      {error ? <StripeFormError>{error}</StripeFormError> : null}
      {renderCoupon && (
        <Coupon
          isMonthlyBilling={isMonthlyBilling}
          onValidateCoupon={handleValidateCoupon}
          currency={currency}
        />
      )}
      {isFeatureEnabled === '12_INSTALLMENTS' && !partner && (
        <ToggleWrapper>
          <Toggle
            value={isMonthlyBilling}
            onChange={(e) => {
              handleMonthlyToggleChange(e)
            }}
          />
          <ToggleLabel boldText={isMonthlyBilling}>{t('bill_monthly')}</ToggleLabel>
        </ToggleWrapper>
      )}
      <CheckoutCreditCardCancelAnytime>{t('cancel_anytime')}</CheckoutCreditCardCancelAnytime>
      {!isFetching && (
        <Button
          css={{ minWidth: isMobile ? '90%' : '383px', height: '30px', marginTop: '24px' }}
          text={buttonText()}
          loading={isLoading}
          disabled={isLoading}
          onClick={() => {
            setIsLoading(true)
            submitRef.current?.click()
          }}
        />
      )}
      <CheckoutBelowButtonWrapper>
        <FontAwesomeIcon icon={faLock as IconProp} />
        <CheckoutBelowButtonText>
          {t('we_keep_your_information_confidential')}
        </CheckoutBelowButtonText>
      </CheckoutBelowButtonWrapper>
    </CheckoutCreditCardDetailsWrapper>
  )
}
